import { api } from "@/lib/api";
import type { GrabRecord, MediaKind } from "./types";

interface ListParams {
  media_kind?: MediaKind;
  media_id?: number;
  limit?: number;
}

/** Blocklist row — one previously-failed release for a media item.
 *  Future grabs filter these out at selection time. */
export interface BlocklistEntry {
  id: number;
  media_kind: "movie" | "episode" | "album";
  media_id: number;
  release_guid: string;
  reason: string | null;
  blocked_at: string;
}

/** Filter shape for a bulk redownload. Maps 1:1 to the Rust
 *  `BulkRedownloadRequest` body. Every field optional except `status`,
 *  which has a server-side default of `imported`. */
export interface BulkRedownloadRequest {
  media_kind?: MediaKind;
  /** Original download_clients.id. The recovery use case sets this
   *  to the embedded-downloader id so only the broken history is
   *  replayed. */
  client_was?: number;
  /** Default `imported`. Operators can flip to `failed` to retry the
   *  truncation-guard rejections. */
  status?: string;
  /** Server defaults to 100; pass an explicit cap from the UI so
   *  long bulk runs don't surprise. */
  limit?: number;
}

/** Per-row outcome of a bulk redownload. Keyed off the original grab id
 *  so the UI can highlight which rows didn't replay cleanly. */
export interface BulkRedownloadDetail {
  grab_id: number;
  release_title: string;
  /** "ok" | "skipped" | "failed". */
  status: string;
  message: string | null;
  /** New grab id when status === "ok". */
  new_grab_id: number | null;
}

/** Aggregate result of a bulk redownload run. Mirrors the Rust
 *  `BulkOutcome`. */
export interface BulkRedownloadOutcome {
  matched: number;
  dispatched: number;
  skipped: number;
  failed: number;
  details: BulkRedownloadDetail[];
}

export const grabsApi = {
  list: (params: ListParams = {}) => {
    const qs = new URLSearchParams();
    if (params.media_kind) qs.set("media_kind", params.media_kind);
    if (params.media_id != null) qs.set("media_id", String(params.media_id));
    if (params.limit != null) qs.set("limit", String(params.limit));
    const suffix = qs.toString();
    return api<GrabRecord[]>(`/api/v1/grabs${suffix ? `?${suffix}` : ""}`);
  },

  blocklist: {
    /** List blocklist entries, optionally filtered to one media item. */
    list: (params: ListParams = {}) => {
      const qs = new URLSearchParams();
      if (params.media_kind) qs.set("media_kind", params.media_kind);
      if (params.media_id != null) qs.set("media_id", String(params.media_id));
      if (params.limit != null) qs.set("limit", String(params.limit));
      const suffix = qs.toString();
      return api<BlocklistEntry[]>(
        `/api/v1/grabs/blocklist${suffix ? `?${suffix}` : ""}`,
      );
    },

    /** Remove a single entry by id. */
    remove: (id: number) =>
      api<void>(`/api/v1/grabs/blocklist/${id}`, { method: "DELETE" }),

    /** Remove every entry for a media item. Returns the count removed. */
    clearForMedia: (mediaKind: MediaKind, mediaId: number) =>
      api<{ removed: number }>(
        `/api/v1/grabs/blocklist?media_kind=${mediaKind}&media_id=${mediaId}`,
        { method: "DELETE" },
      ),
  },

  /** Re-dispatch a single previously-grabbed release through whichever
   *  download client is currently enabled. Returns the freshly-created
   *  grab record. The original row is left as historical context. */
  redownload: (id: number) =>
    api<GrabRecord>(`/api/v1/grabs/${id}/redownload`, { method: "POST" }),

  /** Filter-driven bulk redownload. Returns a summary the UI can show
   *  inline. Long runs block the request — the UI should surface the
   *  spinner accordingly. */
  bulkRedownload: (req: BulkRedownloadRequest) =>
    api<BulkRedownloadOutcome>(`/api/v1/grabs/bulk-redownload`, {
      method: "POST",
      body: JSON.stringify(req),
      headers: { "Content-Type": "application/json" },
    }),
};
